import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from '../../lib/axios';
import { palette } from '../../theme/colors';
import {
  CashDrawerShiftSummary,
  CashDrawerShiftStatus,
  CashMovementType,
  CashMovementReason,
} from '../../types/cashDrawer';

const fetchShiftSummary = async (id: string): Promise<CashDrawerShiftSummary> => {
  const response = await axiosInstance.get(`/cash-drawer/shifts/${id}`);
  return response.data;
};

const formatCurrency = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('fr-DZ', {
    style: 'currency',
    currency: 'DZD',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getUserLabel = (user?: { firstName?: string; lastName?: string; email?: string } | null) => {
  if (!user) return '—';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || '—';
};

export const CashDrawerShow = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const getReasonLabel = (reason: CashMovementReason): string => {
    const labels: Record<CashMovementReason, string> = {
      [CashMovementReason.CHANGE_FUND]: t('cashDrawer.movementReason.changeFund'),
      [CashMovementReason.PETTY_EXPENSE]: t('cashDrawer.movementReason.pettyExpense'),
      [CashMovementReason.BANK_DEPOSIT]: t('cashDrawer.movementReason.bankDeposit'),
      [CashMovementReason.CORRECTION]: t('cashDrawer.movementReason.correction'),
      [CashMovementReason.OTHER]: t('cashDrawer.movementReason.other'),
    };
    return labels[reason] || reason;
  };

  const {
    data: shift,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['cash-drawer-shift', id],
    queryFn: () => fetchShiftSummary(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/cash-drawer')}>
            <ArrowBackIcon />
          </IconButton>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !shift) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/app/cash-drawer')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t('cashDrawer.show.notFound')}</Typography>
        </Box>
        <Card>
          <CardContent>
            <Typography color="error">{t('cashDrawer.show.notFoundDetail')}</Typography>
            <Button variant="contained" onClick={() => navigate('/app/cash-drawer')} sx={{ mt: 2 }}>
              {t('cashDrawer.show.backToList')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isOpen = shift.status === CashDrawerShiftStatus.OPEN;
  const discrepancy = shift.discrepancy;
  const discrepancyColor =
    discrepancy === null || discrepancy === undefined || discrepancy === 0
      ? palette.textPrimary
      : discrepancy > 0
        ? palette.success
        : palette.error;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/app/cash-drawer')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">{t('cashDrawer.show.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cashDrawer.list.openedAt')}: {formatDateTime(shift.openedAt)}
          </Typography>
        </Box>
        <Chip
          label={isOpen ? t('cashDrawer.status.open') : t('cashDrawer.status.closed')}
          color={isOpen ? 'success' : 'default'}
          variant={isOpen ? 'filled' : 'outlined'}
          sx={{ ml: 'auto' }}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cashDrawer.show.balanceBreakdown')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('cashDrawer.openingBalance')}</Typography>
                  <Typography fontWeight={600}>{formatCurrency(shift.openingBalance)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">
                    {t('cashDrawer.expectedClosingBalance')}
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatCurrency(shift.expectedClosingBalance)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">
                    {t('cashDrawer.countedClosingBalance')}
                  </Typography>
                  <Typography fontWeight={600}>
                    {formatCurrency(shift.countedClosingBalance)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" pt={1} borderTop={1} borderColor="divider">
                  <Typography color="text.secondary">{t('cashDrawer.discrepancy')}</Typography>
                  <Typography fontWeight={700} sx={{ color: discrepancyColor }}>
                    {discrepancy !== null && discrepancy !== undefined && discrepancy > 0 ? '+' : ''}
                    {formatCurrency(discrepancy)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('cashDrawer.list.openedBy')}: {getUserLabel(shift.openedByUser)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('cashDrawer.list.closedBy')}: {getUserLabel(shift.closedByUser)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('cashDrawer.list.closedAt')}: {formatDateTime(shift.closedAt)}
                </Typography>
                {shift.notes && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: palette.surfaceElevated }}>
                    <Typography variant="body2">{shift.notes}</Typography>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cashDrawer.show.movements')}
              </Typography>
              {shift.movements && shift.movements.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('cashDrawer.show.movementDate')}</TableCell>
                        <TableCell>{t('cashDrawer.show.movementType')}</TableCell>
                        <TableCell>{t('cashDrawer.show.movementReason')}</TableCell>
                        <TableCell align="right">{t('cashDrawer.show.movementAmount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shift.movements.map(movement => (
                        <TableRow key={movement.id} hover>
                          <TableCell>{formatDateTime(movement.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                movement.type === CashMovementType.CASH_IN
                                  ? t('cashDrawer.movement.cashIn')
                                  : t('cashDrawer.movement.cashOut')
                              }
                              size="small"
                              color={movement.type === CashMovementType.CASH_IN ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{getReasonLabel(movement.reason)}</TableCell>
                          <TableCell align="right">{formatCurrency(movement.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center', bgcolor: palette.surfaceElevated, borderRadius: 2 }}>
                  <Typography color="text.secondary">{t('cashDrawer.show.noMovements')}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CashDrawerShow;
